import { Fragment, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../api/client';
import {
  PageHeader, Table, Th, Td, TrEmpty, Badge, Btn, StatCard,
  LoadingState, ErrorState,
} from '../components/ui';

// ── Types ─────────────────────────────────────────────────────────────────────

type WaitlistStatus = 'pending' | 'invited' | 'accepted' | 'declined' | 'removed';

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  company: string;
  role: string;
  teamSize: string;
  status: WaitlistStatus;
  createdAt: string;
  whyBeta: string;
  painPoints: string[];
  techStack: string;
  emailUpdates: boolean;
}

interface WaitlistStats {
  total: number;
  pending: number;
  invited: number;
  accepted: number;
  declined: number;
}

interface WaitlistResponse {
  entries: WaitlistEntry[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const STATUS_VARIANT: Record<WaitlistStatus, 'warning' | 'info' | 'success' | 'default' | 'danger'> = {
  pending:  'warning',
  invited:  'info',
  accepted: 'success',
  declined: 'default',
  removed:  'danger',
};

// ── Confirm dialog (reuses Users.tsx pattern) ─────────────────────────────────

function ConfirmDialog({
  title, body, confirmLabel, onConfirm, onCancel, loading,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <div className="relative bg-bg-surface border border-white/[0.06] rounded-2xl p-6 w-full max-w-sm shadow-2xl z-10">
        <h3 className="font-semibold text-white mb-2">{title}</h3>
        <p className="text-sm text-gray-400 mb-5">{body}</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm bg-white/[0.06] text-gray-300 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Expanded row ──────────────────────────────────────────────────────────────

function ExpandedRow({ entry, cols }: { entry: WaitlistEntry; cols: number }) {
  return (
    <tr className="bg-white/[0.015]">
      <td colSpan={cols} className="px-6 py-4 border-b border-white/[0.04]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {/* Why beta */}
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
              Why they want beta access
            </p>
            <p className="text-gray-300 leading-relaxed">{entry.whyBeta || '—'}</p>
          </div>

          {/* Tech stack */}
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
              Tech stack
            </p>
            <p className="text-gray-300">{entry.techStack || '—'}</p>
          </div>

          {/* Pain points */}
          {entry.painPoints && entry.painPoints.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                Pain points
              </p>
              <div className="flex flex-wrap gap-1.5">
                {entry.painPoints.map((p, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded-md text-xs bg-white/[0.06] text-gray-300 border border-white/[0.08]"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Email updates */}
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
              Email updates
            </p>
            <p className="text-gray-300">{entry.emailUpdates ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Row actions ───────────────────────────────────────────────────────────────

function RowActions({ entry }: { entry: WaitlistEntry }) {
  const qc = useQueryClient();
  const [inviteDialog, setInviteDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['admin', 'waitlist'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'waitlist', 'stats'] });
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const invite = useMutation({
    mutationFn: () => api.post(`/waitlist/${entry.id}/invite`),
    onSuccess: () => {
      setInviteDialog(false);
      invalidate();
      showToast(`${entry.name} invited successfully`);
    },
  });

  const decline = useMutation({
    mutationFn: () => api.put(`/waitlist/${entry.id}/status`, { status: 'declined' }),
    onSuccess: () => { invalidate(); },
  });

  const remove = useMutation({
    mutationFn: () => api.put(`/waitlist/${entry.id}/status`, { status: 'removed' }),
    onSuccess: () => { invalidate(); },
  });

  const permanentDelete = useMutation({
    mutationFn: () => api.delete(`/waitlist/${entry.id}`),
    onSuccess: () => {
      setDeleteDialog(false);
      invalidate();
      showToast(`${entry.name} permanently deleted`);
    },
  });

  const isPending = invite.isPending || decline.isPending || remove.isPending || permanentDelete.isPending;

  return (
    <>
      {toast && (
        <div className="fixed bottom-4 right-4 z-50 px-4 py-2.5 bg-emerald-500/20 border border-emerald-500/30 rounded-xl text-sm text-emerald-400 shadow-xl">
          {toast}
        </div>
      )}

      <div className="flex items-center gap-1.5">
        {entry.status === 'pending' && (
          <>
            <Btn
              variant="primary"
              disabled={isPending}
              onClick={() => setInviteDialog(true)}
            >
              Invite
            </Btn>
            <Btn
              variant="ghost"
              disabled={isPending}
              onClick={() => decline.mutate()}
            >
              Decline
            </Btn>
          </>
        )}
        {entry.status !== 'removed' && (
          <Btn
            variant="danger"
            disabled={isPending}
            onClick={() => remove.mutate()}
          >
            Remove
          </Btn>
        )}
        {entry.status === 'removed' && (
          <Btn
            variant="danger"
            disabled={isPending}
            onClick={() => setDeleteDialog(true)}
          >
            Delete permanently
          </Btn>
        )}
      </div>

      {inviteDialog && (
        <ConfirmDialog
          title={`Invite ${entry.name}?`}
          body={`This will create an entity named "${entry.company}", provision a user account, and send a password-reset email to ${entry.email}.`}
          confirmLabel="Invite"
          loading={invite.isPending}
          onConfirm={() => invite.mutate()}
          onCancel={() => setInviteDialog(false)}
        />
      )}

      {deleteDialog && (
        <ConfirmDialog
          title={`Permanently delete ${entry.name}?`}
          body={`This will permanently remove ${entry.email} from the waitlist. This action cannot be undone.`}
          confirmLabel="Delete permanently"
          loading={permanentDelete.isPending}
          onConfirm={() => permanentDelete.mutate()}
          onCancel={() => setDeleteDialog(false)}
        />
      )}
    </>
  );
}

// ── WaitlistPage ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS: Array<{ value: WaitlistStatus | 'all'; label: string }> = [
  { value: 'all',      label: 'All' },
  { value: 'pending',  label: 'Pending' },
  { value: 'invited',  label: 'Invited' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'removed',  label: 'Removed' },
];

export function Waitlist() {
  const [statusFilter, setStatusFilter] = useState<WaitlistStatus | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: stats, isLoading: statsLoading } = useQuery<WaitlistStats>({
    queryKey: ['admin', 'waitlist', 'stats'],
    queryFn: () => api.get('/waitlist/stats'),
  });

  const { data, isLoading, error } = useQuery<WaitlistResponse>({
    queryKey: ['admin', 'waitlist', statusFilter],
    queryFn: () =>
      api.get(
        `/waitlist?${statusFilter !== 'all' ? `status=${statusFilter}&` : ''}limit=50&offset=0`
      ),
  });

  const entries = data?.entries ?? [];

  const COLS = 8;

  return (
    <div>
      <PageHeader
        title="Waitlist"
        subtitle="Private beta applicants"
      />

      {/* Stats row */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Pending" value={stats.pending} variant={stats.pending > 0 ? 'warning' : 'default'} />
          <StatCard label="Invited" value={stats.invited} />
          <StatCard label="Accepted" value={stats.accepted} />
          <StatCard label="Declined" value={stats.declined} />
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value as WaitlistStatus | 'all');
              setExpandedId(null);
            }}
            className="appearance-none bg-bg-surface border border-white/[0.06] rounded-xl px-3 py-2 pr-8 text-sm text-gray-200 focus:outline-none focus:border-primary/50 transition-colors cursor-pointer"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
      </div>

      {/* Table */}
      {isLoading && <LoadingState />}
      {error && <ErrorState message="Error loading waitlist" />}

      {!isLoading && !error && (
        <Table>
          <thead>
            <tr>
              <Th className="w-6"></Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Company</Th>
              <Th>Role</Th>
              <Th>Team Size</Th>
              <Th>Status</Th>
              <Th>Submitted</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <TrEmpty cols={COLS} message="No entries" />
            ) : (
              entries.map(entry => {
                const isExpanded = expandedId === entry.id;
                return (
                  <Fragment key={entry.id}>
                    <tr
                      className={`cursor-pointer transition-colors ${isExpanded ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'}`}
                      onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    >
                      <Td className="text-gray-500 w-6">
                        {isExpanded
                          ? <ChevronDown className="h-3.5 w-3.5" />
                          : <ChevronRight className="h-3.5 w-3.5" />
                        }
                      </Td>
                      <Td className="font-medium text-white">{entry.name}</Td>
                      <Td className="text-gray-400 text-xs font-mono">{entry.email}</Td>
                      <Td>{entry.company}</Td>
                      <Td className="text-gray-400">{entry.role}</Td>
                      <Td className="text-gray-400">{entry.teamSize}</Td>
                      <Td>
                        <Badge variant={STATUS_VARIANT[entry.status]}>
                          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        </Badge>
                      </Td>
                      <Td className="text-gray-400 text-xs tabular-nums whitespace-nowrap">
                        {relativeTime(entry.createdAt)}
                      </Td>
                      <td className="px-4 py-3 text-sm" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <RowActions entry={entry} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <ExpandedRow key={`${entry.id}-expanded`} entry={entry} cols={COLS} />
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
}
